import React, { useState, useEffect } from 'react';
import { MaintenanceRequest, Property, MaintenanceCompany } from '../types';
import { api } from '../services/api';
import { Wrench, Calendar, AlertTriangle, FileText, Briefcase } from 'lucide-react';

interface MaintenanceFormProps {
  onCancel: () => void;
  onSubmit: (data: MaintenanceRequest) => void;
}

const MaintenanceForm: React.FC<MaintenanceFormProps> = ({ onCancel, onSubmit }) => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [companies, setCompanies] = useState<MaintenanceCompany[]>([]);
  const [formData, setFormData] = useState<Partial<MaintenanceRequest>>({
    status: 'Pending',
    priority: 'Medium',
    dateReported: new Date().toISOString().split('T')[0],
    companyId: ''
  });

  useEffect(() => {
    Promise.all([
        api.properties.getAll(),
        api.maintenance.companies.getAll()
    ]).then(([props, comps]) => {
        setProperties(props);
        setCompanies(comps);
    });
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
        const newData = { ...prev, [name]: value };
        if (name === 'propertyId') newData.unitId = '';
        return newData;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const prop = properties.find(p => p.id === formData.propertyId);
    onSubmit({ 
        ...formData, 
        id: Math.random().toString(), 
        propertyName: prop?.name || '', 
    } as MaintenanceRequest);
  };

  const inputClass = "block w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-sm";

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn overflow-hidden">
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] border border-slate-200 animate-scaleIn">
            <div className="bg-white p-6 border-b border-slate-200 flex justify-between items-center shrink-0">
                <h2 className="text-xl font-black text-slate-800 flex items-center gap-2">
                    <div className="bg-fly-50 p-2 rounded-lg">
                        <Wrench className="w-5 h-5 text-fly-600" />
                    </div>
                    <span>طلب صيانة جديد</span>
                </h2>
                <button type="button" onClick={onCancel} className="p-2 bg-slate-50 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all border border-slate-100">✕</button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6 custom-scrollbar bg-slate-50/50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                     <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">العقار</label>
                        <select name="propertyId" value={formData.propertyId || ''} onChange={handleInputChange} className={inputClass} required>
                            <option value="">إختر العقار</option>
                            {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                     </div>
                     <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">الوحدة (اختياري)</label>
                        <select name="unitId" value={formData.unitId || ''} onChange={handleInputChange} className={inputClass} disabled={!formData.propertyId}>
                            <option value="">إختر الوحدة</option>
                            {properties.find(p => String(p.id) === String(formData.propertyId))?.units.map(u => (
                                <option key={u.id} value={u.id}>{u.unitNumber}</option>
                            ))}
                        </select>
                     </div>
                </div>

                <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">وصف المشكلة</label>
                    <textarea name="description" rows={3} onChange={handleInputChange} className={inputClass} placeholder="أدخل تفاصيل العطل..." required></textarea>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                     <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">إسناد لشركة (اختياري)</label>
                        <select name="companyId" onChange={handleInputChange} className={inputClass}>
                            <option value="">-- بدون شركة --</option>
                            {companies.map(c => <option key={c.id} value={c.id}>{c.name} ({c.specialty})</option>)}
                        </select>
                     </div>
                    <div>
                         <label className="block text-sm font-bold text-slate-700 mb-2">تاريخ البلاغ</label>
                         <input type="date" name="dateReported" value={formData.dateReported} onChange={handleInputChange} className={inputClass} />
                    </div>
                </div>
                 <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">الأولوية</label>
                     <div className="flex gap-4">
                        {['Low', 'Medium', 'High'].map(p => (
                            <label key={p} className={`flex-1 cursor-pointer border rounded-xl p-3 text-center transition-all ${formData.priority === p ? 'bg-fly-50 border-fly-500 text-fly-700 font-bold' : 'border-slate-250 bg-white hover:border-fly-200'}`}>
                                <input type="radio" name="priority" value={p} checked={formData.priority === p} onChange={handleInputChange} className="hidden" />
                                {p === 'High' ? 'طارئ 🚨' : p === 'Medium' ? 'متوسط ⚠️' : 'عادي 🟢'}
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            <div className="p-5 bg-white border-t border-slate-200 flex justify-center gap-4 shrink-0">
                <button type="button" onClick={onCancel} className="flex-1 sm:flex-none px-8 py-3 bg-white border border-slate-300 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-all text-sm font-bold">إلغاء</button>
                <button type="submit" className="flex-1 sm:flex-none px-12 py-3 bg-fly-600 text-white font-bold rounded-xl hover:bg-fly-700 shadow-lg shadow-fly-600/20 transition-all text-sm font-bold">حفظ الطلب</button>
            </div>
        </form>
    </div>
  );
};

export default MaintenanceForm;